import { createTelemetry, type UsageEventInput } from "@atlas/shared";
import type { AnyRouter } from "@tanstack/react-router";
import { queryClient } from "./query-client";
import { API_BASE_URL, clientHeaders } from "./api-client";
import type { User } from "../api/auth";

// First-party screen-view telemetry (spec 14 / ATL-70). Reports the matched
// route *pattern* (never the concrete URL) for signed-in users only; the
// admin Usage tab reads it back. Anonymous traffic stays with Umami.

function isSignedIn(): boolean {
  const me = queryClient.getQueryData<{ user: User }>(["me"]);
  return !!me?.user;
}

async function send(events: UsageEventInput[]): Promise<void> {
  // keepalive lets the final flush on pagehide complete after the tab goes;
  // unlike sendBeacon it can still carry our headers cross-origin.
  const res = await fetch(`${API_BASE_URL}/telemetry/events`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json", ...clientHeaders() },
    body: JSON.stringify({ client: "web", events }),
  });
  // 401 = signed out between queueing and flushing: drop, don't retry.
  if (!res.ok && res.status !== 401 && res.status !== 400) {
    throw new Error(`telemetry ${res.status}`);
  }
}

export const telemetry = createTelemetry({
  client: "web",
  send,
  isEnabled: isSignedIn,
});

let installed = false;

/** Wire the router + page lifecycle. Idempotent; call once from main.tsx. */
export function installTelemetry(router: AnyRouter): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const report = () => {
    const matches = router.state.matches;
    const leaf = matches[matches.length - 1];
    if (!leaf) return;
    telemetry.screen(leaf.fullPath);
  };

  router.subscribe("onResolved", report);
  // The first route resolves before subscribers can attach in some builds.
  if (router.state.status === "idle") report();

  const flush = () => void telemetry.flush();
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
